import styled from "@emotion/styled";
import { useState, useRef } from "react";
import { COLORS, dateFormatter, nameFormatter, priceFormatter, priceFormatterV3 } from "../../settings/themeSettings";
import { THEME_COLORS } from "../../constants/themeColors";
import "./styledTable.scss";
import "../../components/StandardizedTable/StandardizedTable.scss"; // Import des nouveaux styles
import {
    TablePagination,
    tablePaginationClasses as classes,
  } from '@mui/base/TablePagination';
import { useNavigate } from "react-router";
import { getCellStyles, detectDataType, getColumnAlignment, calculateColumnWidth, isPercentageColumn, formatPercentageValue } from "../../utils/tableUtils";

import _ from "lodash";


// Couleurs du thème de l'application
const themeColors = {
    primary: THEME_COLORS.PRIMARY,
    primaryDark: THEME_COLORS.PRIMARY_DARK,
    primaryLight: THEME_COLORS.PRIMARY_LIGHT,
    white: THEME_COLORS.WHITE,
  };
  
  const grey = {
    50: '#F3F6F9',
    100: '#E7EBF0',
    200: '#E0E3E7',
    300: '#CDD2D7',
    400: '#B2BAC2',
    500: '#A0AAB4',
    600: '#6F7E8C',
    700: '#3E5060',
    800: '#2D3843',
    900: '#1A2027',
  };




const Root = styled('div')(
    ({ theme, maxHeight }) => `
    width: 100%;
    max-height: ${maxHeight || '70vh'};
    overflow-x: auto;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    position: relative;
    border-radius: 8px;

    /* Personnalisation du scrollbar pour un look plus moderne */
    &::-webkit-scrollbar {
      width: 8px;
      height: 8px;
    }
    &::-webkit-scrollbar-track {
      background: ${grey[50]};
      border-radius: 10px;
    }
    &::-webkit-scrollbar-thumb {
      background: ${grey[300]};
      border-radius: 10px;
      &:hover {
        background: ${grey[400]};
      }
    }

    table {
      font-family: IBM Plex Sans, sans-serif;
      font-size: 0.875rem;
      border-collapse: collapse;
      width: 100%;
      min-width: 800px; /* Garantit une largeur minimale pour le défilement horizontal */
    }
  
    td,
    th {
      border: 1px solid ${theme.palette.mode === 'dark' ? grey[800] : grey[200]};
      text-align: left;
      padding: 6px;
      color : ${themeColors.primary};
      text-transform : capitalize;
      font-weight: normal;
      
      &.percentage-cell {
        text-align: center !important;
        font-weight: bold;
        background-color: ${themeColors.primaryLight}15;
        color: ${themeColors.primary} !important;
      }
      
      &.total-cell {
        background-color: ${themeColors.primary} !important;
        color: ${themeColors.white} !important;
        font-weight: bold;
        
        &.percentage-cell {
          text-align: center !important;
          background-color: ${themeColors.primary} !important;
          color: ${themeColors.white} !important;
        }
      }
    }
  

    th {
      background-color: ${themeColors.primary};
      color: ${themeColors.white};
      background-image: linear-gradient(135deg, ${themeColors.primary} 0%, ${themeColors.primaryDark} 100%);
      text-align: center; /* Force le centrage des headers */
      
      /* Important: Les headers ne doivent jamais hériter des styles de pourcentage */
      &.percentage-cell {
        background-color: ${themeColors.primary} !important;
        background-image: linear-gradient(135deg, ${themeColors.primary} 0%, ${themeColors.primaryDark} 100%) !important;
        color: ${themeColors.white} !important;
        text-align: center !important;
        font-weight: 600 !important;
      }
    }
    `,
  );
  
  const CustomTablePagination = styled(TablePagination)(
    ({ theme }) => `
    & .${classes.spacer} {
      display: none;
      color: ${themeColors.primary};
    }
  
    & .${classes.toolbar}  {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 10px;
      color: ${themeColors.primary};
  
      @media (min-width: 768px) {
        flex-direction: row;
        align-items: center;
      }
    }
  
    & .${classes.selectLabel} {
      color: ${themeColors.primary};
      margin: 0;
    }
  
    & .${classes.select}{
      padding: 2px;
      border: 1px solid ${theme.palette.mode === 'dark' ? grey[800] : grey[200]};
      border-radius: 50px;
      background-color: transparent;
      color: ${themeColors.primary};
  
      &:hover {
        background-color: ${theme.palette.mode === 'dark' ? grey[800] : grey[50]};
      }
  
      &:focus {
        outline: 1px solid ${themeColors.primary};
        background-color: ${themeColors.primaryLight};
      }
    }
  
    & .${classes.displayedRows} {
      margin: 0;
      color: ${themeColors.primary};

      @media (min-width: 768px) {
        margin-left: auto;
      }
    }
  
    & .${classes.actions} {
      padding: 2px;
      border: 1px solid ${theme.palette.mode === 'dark' ? grey[800] : grey[200]};
      border-radius: 50px;
      text-align: center;
    }
  
    & .${classes.actions} > button {
      margin: 0 8px;
      border: transparent;
      border-radius: 2px;
      background-color: transparent;
      color: ${themeColors.primary};
      
      & > * {
        color: ${themeColors.primary};
      }

      &:hover {
        background-color: ${themeColors.primaryLight};
      }
  
      &:focus {
        outline: 1px solid ${themeColors.primary};
        background-color: ${themeColors.primaryLight};
      }
    }
    `,
  );
  



const UpdatedTable = ({ head = [], rows = [], totals = [], headSubs = {}, totalPosition = 'bottom', maxHeight }) => {

    const [page, setPage] = useState(0);
    const navigateTo = useNavigate();

    // Hook pour la détection automatique des pourcentages supprimé
    // Fonctionnalité maintenant gérée directement dans les composants DashboardTable
    const tableRef = useRef(null);


    // Avoid a layout jump when reaching the last page with empty rows.

    const renderTotalRow = () => {
        if (totals.length === 0) return null;

        return (
            <tr style={{ color: themeColors.white, background: themeColors.primary }}>
                {
                    head.map((h, idx) => {
                        const isFirstColumn = idx === 0;
                        const totalValue = totals.includes(h) ? _.sumBy(rows, h) : 0;
                        const cellStyles = getCellStyles(h, totalValue, false, true, null, rows);
                        const columnWidth = calculateColumnWidth(h, rows, h);
                        const isPctColumn = isPercentageColumn(h, totalValue, rows);

                        return (
                            <td
                                key={idx}
                                className={isPctColumn ? 'percentage-cell total-cell' : 'total-cell'}
                                style={{
                                    ...cellStyles,
                                    color: themeColors.white,
                                    background: themeColors.primary,
                                    width: columnWidth.width,
                                    minWidth: columnWidth.minWidth,
                                    textAlign: isFirstColumn ? 'left' : (isPctColumn ? 'center' : 'right'),
                                    fontWeight: 'bold',
                                    position: 'sticky',
                                    bottom: totalPosition === 'bottom' ? 0 : 'auto',
                                    zIndex: 105
                                }}
                            >
                                {isFirstColumn ? 'Total' : (totals.includes(h) ? priceFormatterV3(totalValue) : '')}
                            </td>
                        );
                    })
                }
            </tr>
        );
    };


    return <Root sx={{ width: '100%', maxWidth: '100%' }} maxHeight={maxHeight}>
        <table ref={tableRef} aria-label="custom pagination table" style={{ width: '100%', tableLayout: 'auto', borderCollapse: 'collapse' }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 100, backgroundColor: themeColors.white }}>
                {/* Ligne de Total affichée AU-DESSUS des en-têtes de colonnes si totalPosition === 'top' */}
                {totalPosition === 'top' && (
                    <tr style={{ background: COLORS.accent, color: themeColors.white }}>
                        {head.map((h, idx) => {
                            const isFirstColumn = idx === 0;
                            const totalValue = totals.includes(h) ? _.sumBy(rows, h) : 0;
                            const columnWidth = calculateColumnWidth(h, rows, h);
                            return (
                                <th
                                    key={`total-${idx}`}
                                    style={{
                                        backgroundColor: COLORS.accent,
                                        color: themeColors.white,
                                        padding: '8px',
                                        textAlign: isFirstColumn ? 'left' : 'right',
                                        fontWeight: 'bold',
                                        width: columnWidth.width,
                                        minWidth: columnWidth.minWidth,
                                        position: 'sticky',
                                        top: 0,
                                        zIndex: 110,
                                        borderBottom: `2px solid ${themeColors.white}`
                                    }}
                                >
                                    {isFirstColumn ? 'TOTAL GÉNÉRAL' : (totals.includes(h) ? priceFormatterV3(totalValue) : '')}
                                </th>
                            );
                        })}
                    </tr>
                )}
                <tr style={{ backgroundColor: themeColors.primary }}>
                    {
                        head.map((title, idx) => {
                            const sampleValue = rows.length > 0 ? rows[0][title] : null;
                            const headerStyles = getCellStyles(title, sampleValue, true, false, null, rows);
                            const displayTitle = headSubs.hasOwnProperty(title) ? headSubs[title] : title;
                            const columnWidth = calculateColumnWidth(title, rows, displayTitle);

                            return (
                                <th
                                    key={`head-${idx}`}
                                    style={{
                                        ...headerStyles,
                                        width: columnWidth.width,
                                        minWidth: columnWidth.minWidth,
                                        position: 'sticky',
                                        top: totalPosition === 'top' ? '40px' : 0, // Si le total est à 0, le header est à 40px
                                        zIndex: 100
                                    }}
                                >
                                    {nameFormatter(displayTitle)}
                                </th>
                            );
                        })
                    }
                </tr>
            </thead>

            <tbody>
                {rows.map((row, idx) => (
                    <tr key={idx} >
                        {
                            head.map((title, titleIdx) => {
                                const value = row[title];
                                const cellStyles = getCellStyles(title, value, false, false, null, rows);
                                const columnWidth = calculateColumnWidth(title, rows, title);
                                const isPctColumn = isPercentageColumn(title, value, rows);

                                return (
                                    <td
                                        key={titleIdx}
                                        className={isPctColumn ? 'percentage-cell' : ''}
                                        style={{
                                            ...cellStyles,
                                            width: columnWidth.width,
                                            minWidth: columnWidth.minWidth,
                                            // Force le centrage et la couleur pour les colonnes de pourcentage
                                            textAlign: isPctColumn ? 'center' : cellStyles.textAlign,
                                            color: isPctColumn ? themeColors.primary : cellStyles.color,
                                            fontWeight: isPctColumn ? 'bold' : cellStyles.fontWeight,
                                            backgroundColor: isPctColumn ? 'transparent' : cellStyles.backgroundColor
                                        }}
                                    >
                                        {isPctColumn
                                            ? formatPercentageValue(value, title)
                                            : (/montant|prix|tarif|depouillement|marge$|CFA$/.test(title)
                                                ? priceFormatterV3(value)
                                                : (/date/.test(title)
                                                    ? dateFormatter(value)
                                                    : value
                                                )
                                            )
                                        }
                                    </td>
                                );
                            })
                        }




                    </tr>
                ))}


                {totalPosition === 'bottom' && renderTotalRow()}


            </tbody>


        </table>
    </Root>
}


export default UpdatedTable;
